import {
  CASH_COMMON_CURRENCIES,
  CASH_DEFAULT_ACCOUNT_CURRENCIES,
  type CashPositionRow,
} from '@felix/domain';
import { Button, Checkbox, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './Cash.module.css';
import { DialogShell, ReasonDialog } from './CashDialogs.js';
import { Panel } from './CtmBoard.js';
import type { CashSource } from './source.js';
import type { CashData, CashFleet } from './types.js';
import { formatMoney } from './view.js';

/**
 * Finance ▸ Cash Accounts (ADR 0110 §1, §3): gemi ve ofis kasaları. Gemide geminin para birimi
 * kasaları (bakiye, yoldaki CTM ve teslimden sonra beklenen); ofiste filo görünümü (gemi × para
 * birimi, birim başına filo toplamı) ve seçili geminin kasası. Kasayı ofis kurar ve kapar, gemi
 * yönetir. Kur çevirisi yok: her birim kendi içinde toplanır.
 */

type Dialog =
  | { kind: 'setup'; open: readonly string[]; configured: boolean; preselect?: string[] }
  | { kind: 'close'; currency: string }
  | null;

/** Geminin para birimi kartları: bakiye, yolda ve beklenen. */
export function PositionCards(props: {
  positions: readonly CashPositionRow[];
  canManage: boolean;
  onClose?: (currency: string) => void;
  onReopen?: (currency: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  return (
    <div className={`${classes.balanceCards} ${classes.accountCards}`}>
      {props.positions.map((p) => (
        <section
          key={p.currency}
          className={classes.balanceCard}
          aria-label={t('cash.accounts.cardLabel', { currency: p.currency })}
          data-closed={p.account === 'closed' || undefined}
        >
          <span className={classes.balanceHead}>
            <span className={classes.currencyIcon} aria-hidden>
              {p.currency.slice(0, 2)}
            </span>
            {p.currency}
            <StatusBadge
              tone={
                p.account === 'open' ? 'success' : p.account === 'closed' ? 'neutral' : 'warning'
              }
            >
              {t(`cash.accounts.states.${p.account}`)}
            </StatusBadge>
          </span>
          <dl className={classes.balanceRows}>
            <dt>{t('cash.accounts.inTransit')}</dt>
            <dd>{formatMoney(p.inTransit, language)}</dd>
            <dt>{t('cash.accounts.expected')}</dt>
            <dd>{formatMoney(p.expected, language)}</dd>
          </dl>
          <span className={classes.balanceTotal} data-low={p.balance <= 0 || undefined}>
            <span>{t('cash.balance.balance')}</span>
            <span>{formatMoney(p.balance, language)}</span>
          </span>
          {props.canManage && p.account === 'open' ? (
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              disabled={p.balance !== 0 || p.inTransit !== 0}
              title={
                p.balance !== 0 || p.inTransit !== 0 ? t('cash.accounts.closeBlocked') : undefined
              }
              onClick={() => props.onClose?.(p.currency)}
            >
              {t('cash.accounts.close')}
            </Button>
          ) : null}
          {props.canManage && p.account !== 'open' ? (
            <Button size="compact-xs" variant="subtle" onClick={() => props.onReopen?.(p.currency)}>
              {t(p.account === 'closed' ? 'cash.accounts.reopen' : 'cash.accounts.addToBox')}
            </Button>
          ) : null}
        </section>
      ))}
    </div>
  );
}

/** Kasa kurulumu ya da birim ekleme: varsayılan EUR, USD, TRY, GBP; başka ISO kodu da. */
function SetupDialog(props: {
  open: readonly string[];
  configured: boolean;
  preselect?: readonly string[] | undefined;
  onClose: () => void;
  onConfirm: (currencies: string[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const options = [
    ...new Set([
      ...CASH_DEFAULT_ACCOUNT_CURRENCIES,
      ...CASH_COMMON_CURRENCIES,
      ...(props.preselect ?? []),
    ]),
  ]
    .filter((c) => !props.open.includes(c))
    .sort();
  // Kurulumda varsayılan dört birim seçili gelir (sahip isteği 2026-10-23)
  const [selected, setSelected] = useState<string[]>(
    props.preselect
      ? [...props.preselect]
      : props.configured
        ? []
        : CASH_DEFAULT_ACCOUNT_CURRENCIES.filter((c) => !props.open.includes(c)).map(String),
  );
  const [other, setOther] = useState('');
  const code = other.trim().toUpperCase();
  const otherValid = code === '' || /^[A-Z]{3}$/.test(code);
  const currencies = [...new Set([...selected, ...(code && otherValid ? [code] : [])])].filter(
    (c) => !props.open.includes(c),
  );
  return (
    <DialogShell
      title={t(props.configured ? 'cash.accounts.addTitle' : 'cash.accounts.setupTitle')}
      text={t('cash.accounts.setupText')}
      confirmLabel={t(props.configured ? 'cash.accounts.add' : 'cash.accounts.setup')}
      disabled={currencies.length === 0 || !otherValid}
      onClose={props.onClose}
      onSubmit={() => props.onConfirm(currencies)}
    >
      <Checkbox.Group label={t('cash.accounts.currencies')} value={selected} onChange={setSelected}>
        <div className={classes.currencyChecks}>
          {options.map((c) => (
            <Checkbox key={c} value={c} label={c} />
          ))}
        </div>
      </Checkbox.Group>
      <TextInput
        label={t('cash.accounts.other')}
        description={t('cash.accounts.otherHint')}
        maxLength={3}
        value={other}
        error={otherValid ? undefined : t('cash.accounts.otherInvalid')}
        onChange={(e) => setOther(e.currentTarget.value)}
      />
    </DialogShell>
  );
}

/**
 * Ofisin filo tablosu (ADR 0110 §3): satır gemi, sütun para birimi; hücrede bakiye ve yoldaki
 * para; alt satırda birim başına filo toplamı.
 */
export function FleetTable(props: {
  fleet: CashFleet;
  selectedId: string | null;
  onSelect?: (vesselId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const currencies = props.fleet.totals.map((total) => total.currency);
  const amount = (balance: number, inTransit: number, closed: boolean) => (
    <>
      <span className={classes.strong}>{formatMoney(balance, language)}</span>
      {inTransit > 0 ? (
        <span className={classes.sub}>
          {t('cash.accounts.inTransitShort', { amount: formatMoney(inTransit, language) })}
        </span>
      ) : null}
      {closed ? <span className={classes.sub}>{t('cash.accounts.states.closed')}</span> : null}
    </>
  );
  return (
    <section
      className={classes.tableWrap}
      aria-label={t('cash.tableOf', { name: t('cash.panels.fleet') })}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={classes.table}>
        <thead>
          <tr>
            <th scope="col">{t('cash.vessel')}</th>
            <th scope="col">{t('cash.accounts.box')}</th>
            {currencies.map((c) => (
              <th key={c} scope="col" className={classes.numeric}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.fleet.vessels.map((v) => (
            <tr key={v.id} data-selected={v.id === props.selectedId || undefined}>
              <th scope="row" className={classes.rowHead}>
                {props.onSelect ? (
                  <button
                    type="button"
                    className={classes.linkButton}
                    aria-pressed={v.id === props.selectedId}
                    onClick={() => props.onSelect?.(v.id)}
                  >
                    {v.name}
                  </button>
                ) : (
                  v.name
                )}
              </th>
              <td>
                <StatusBadge tone={v.configured ? 'success' : 'neutral'}>
                  {t(v.configured ? 'cash.accounts.boxReady' : 'cash.accounts.boxMissing')}
                </StatusBadge>
              </td>
              {currencies.map((c) => {
                const p = v.positions.find((x) => x.currency === c);
                return (
                  <td key={c} className={classes.numeric}>
                    {p ? amount(p.balance, p.inTransit, p.account === 'closed') : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={classes.totalRow}>
            <th scope="row" colSpan={2}>
              {t('cash.accounts.fleetTotal')}
            </th>
            {props.fleet.totals.map((total) => (
              <td key={total.currency} className={classes.numeric}>
                {amount(total.balance, total.inTransit, false)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

export function CashAccounts(props: {
  data: CashData;
  source: CashSource;
  onDone: (text: string) => Promise<void>;
  /** Ofiste filo tablosundan gemi seçimi. */
  onSelectVessel?: (vesselId: string) => void;
}) {
  const { t } = useTranslation();
  const { data, source } = props;
  const office = source.office;
  const web = data.scope === 'web';
  const [fleet, setFleet] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; value: CashFleet }
  >({ kind: 'loading' });
  const [dialog, setDialog] = useState<Dialog>(null);

  const loadFleet = useCallback(async () => {
    if (!web || !office?.fleet) return;
    try {
      setFleet({ kind: 'ready', value: await office.fleet() });
    } catch (error) {
      setFleet({ kind: 'error', code: errorCode(error) });
    }
  }, [web, office]);
  useEffect(() => {
    void loadFleet();
  }, [loadFleet]);

  const positions = data.positions ?? [];
  const configured = data.configured === true;
  const fleetVessel =
    fleet.kind === 'ready' ? fleet.value.vessels.find((v) => v.id === data.vessel.id) : undefined;
  const canManage = Boolean(web && office?.openAccounts && fleetVessel?.canManage);
  const openCurrencies = (data.accounts ?? []).filter((a) => a.open).map((a) => a.currency);

  const changed = async (text: string) => {
    setDialog(null);
    await props.onDone(text);
    await loadFleet();
  };

  const vesselPanel = (
    <Panel
      title={
        web
          ? t('cash.panels.vesselAccounts', { vessel: data.vessel.name })
          : t('cash.panels.accounts')
      }
      actions={
        canManage ? (
          <Button
            size="xs"
            variant="white"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDialog({ kind: 'setup', open: openCurrencies, configured })}
          >
            {t(configured ? 'cash.accounts.add' : 'cash.accounts.setup')}
          </Button>
        ) : undefined
      }
    >
      {!configured ? (
        <p className={classes.note}>
          {t(web ? 'cash.accounts.notConfiguredOffice' : 'cash.accounts.notConfiguredShip')}
        </p>
      ) : null}
      {positions.length === 0 ? (
        <p className={classes.note}>{t('cash.empty.noAccount')}</p>
      ) : (
        <PositionCards
          positions={positions}
          canManage={canManage}
          onClose={(currency) => setDialog({ kind: 'close', currency })}
          onReopen={(currency) =>
            setDialog({
              kind: 'setup',
              open: openCurrencies,
              configured: true,
              preselect: [currency],
            })
          }
        />
      )}
      <p className={classes.rule}>{t('cash.accounts.rule')}</p>
    </Panel>
  );

  const fleetPanel = web ? (
    <Panel title={t('cash.panels.fleet')}>
      {fleet.kind === 'loading' ? (
        <TableSkeleton columns={5} label={t('cash.panels.fleet')} />
      ) : fleet.kind === 'error' ? (
        <LoadError
          title={t('cash.errors.load')}
          code={fleet.code}
          onRetry={() => void loadFleet()}
        />
      ) : (
        <FleetTable
          fleet={fleet.value}
          selectedId={data.vessel.id}
          {...(props.onSelectVessel ? { onSelect: props.onSelectVessel } : {})}
        />
      )}
      <p className={classes.note}>{t('cash.accounts.noConversion')}</p>
    </Panel>
  ) : null;

  const dialogNode =
    dialog?.kind === 'setup' ? (
      <SetupDialog
        open={dialog.open}
        configured={dialog.configured}
        preselect={dialog.preselect}
        onClose={() => setDialog(null)}
        onConfirm={async (list) => {
          await office?.openAccounts?.(data.vessel.id, list);
          await changed(t('cash.notices.accountsOpened', { list: list.join(', ') }));
        }}
      />
    ) : dialog?.kind === 'close' ? (
      <ReasonDialog
        title={t('cash.accounts.closeTitle', { currency: dialog.currency })}
        text={t('cash.accounts.closeText')}
        label={t('cash.accounts.closeReason')}
        confirmLabel={t('cash.accounts.close')}
        onClose={() => setDialog(null)}
        onConfirm={async (reason) => {
          await office?.closeAccount?.(data.vessel.id, dialog.currency, reason);
          await changed(t('cash.notices.accountClosed', { currency: dialog.currency }));
        }}
      />
    ) : null;

  return (
    <div className={classes.stack}>
      {fleetPanel}
      {vesselPanel}
      {dialogNode}
    </div>
  );
}
